import React from 'react';
import { Card, CardContent, CardMedia, Grid } from '@material-ui/core';

import { useCurrentUser } from '@klp/auth';
import type { Bygg } from '@klp/bygg/classes';
import { Block, Heading, Typography } from '@klp/design';
import { useKlpStyles } from '@klp/design/Theme';
import { GoogleMaps } from '@klp/googlemaps';
import { nl2br } from '@klp/helpers';
import { KeyValueItem, KeyValueItems } from '@klp/keyvalueitem';
import { KontraktAccess } from '@klp/levendekontrakter';

import { ByggEnergyScore } from '../../ByggEnergyScore';
import { ByggKontraktList } from '../../ByggKontrakt';

interface IOwnProps {
    bygg: Bygg;
}

export type IByggOverviewProps = IOwnProps;

const byggOverviewStyles = () => ({
    card: {
        borderStyle: 'none',
        marginBottom: '30px',
        padding: 0,
    },
    cardContent: {
        position: 'relative',
        paddingBottom: '75% !important',
    },
    cardMedia: {
        paddingBottom: '75%',
        position: 'relative',
    },
});

export const ByggOverview: React.FC<IByggOverviewProps> = ({ bygg }) => {
    const classes = useKlpStyles({}, byggOverviewStyles);
    const { currentUser: user } = useCurrentUser();

    const keyValueFields = [
        { key: 'navn', titleKey: 'bygg.name', dataCy: 'ByggOverviewByggNavn' },
        {
            key: 'adresse',
            titleKey: 'bygg.adresse',
            dataCy: 'ByggOverviewByggAdresse',
        },
        {
            key: 'byggNummer',
            titleKey: 'bygg.byggNummer',
            dataCy: 'ByggOverviewByggNummer',
        },
        {
            key: 'etasjer',
            titleKey: 'bygg.etasjer',
            dataCy: 'ByggOverviewByggEtasjer',
        },
        {
            key: 'byggeAar',
            titleKey: 'bygg.byggeAar',
            dataCy: 'ByggOverviewByggByggeAar',
        },
        {
            key: 'eiendomsNummer',
            titleKey: 'bygg.eiendomsNummer',
            dataCy: 'ByggOverviewEiendomsNummer',
        },
        {
            key: 'eiendomsansv',
            titleKey: 'bygg.eiendomsAnsvarlig',
            dataCy: 'ByggOverviewEiendomsAnsvarlig',
        },
        {
            key: 'driftsansv',
            titleKey: 'bygg.driftsAnsvarlig',
            dataCy: 'ByggOverviewDriftsAnsvarlig',
        },
        {
            key: 'driftsteknikker',
            titleKey: 'bygg.driftsTekniker',
            dataCy: 'ByggOverviewDriftsTekniker',
        },
        {
            key: 'gabNummer',
            titleKey: 'bygg.gabNummer',
            dataCy: 'ByggOverviewGabNummer',
        },
        {
            key: 'kompleksNummer',
            titleKey: 'bygg.kompleksNummer',
            dataCy: 'ByggOverviewKompleksNummer',
        },
        {
            key: 'totAreal',
            titleKey: 'bygg.totAreal',
            dataCy: 'ByggOverviewTotaltAreal',
            format: (value: any) => `${value} ${bygg.totArealUnits}`,
        },
        {
            key: 'energiKarakter',
            titleKey: 'bygg.energiKarakter',
            dataCy: 'ByggOverviewEnergiKarakter',
        },
        {
            key: 'energiForbruk',
            titleKey: 'bygg.energiForbruk',
            dataCy: 'ByggOverviewEnergiForbruk',
            condition: (value: any) => value > 0,
            format: (value: any) => `${value} ${bygg.energiForbrukUnits}`,
        },
    ];

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} md={9} xs={12}>
                {bygg.beskrivelse && (
                    <>
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="bygg.beskrivelse"
                        />
                        <Typography
                            klpTypographyVariant="small"
                            marginVertical="bulky"
                        >
                            {nl2br(bygg.beskrivelse)}
                        </Typography>
                    </>
                )}
                <Block marginVertical="bulky">
                    <KeyValueItems>
                        {keyValueFields.map((field) => {
                            const value = bygg[field.key as keyof Bygg];
                            const shouldRender = field.condition
                                ? field.condition(value)
                                : value !== null &&
                                  value !== undefined &&
                                  value !== '';

                            if (!shouldRender) return null;

                            const displayValue = field.format
                                ? field.format(value)
                                : value;

                            return (
                                <KeyValueItem
                                    key={field.key}
                                    title={field.titleKey}
                                >
                                    {displayValue}
                                </KeyValueItem>
                            );
                        })}
                    </KeyValueItems>
                </Block>
            </Grid>

            <Grid item={true} md={3} xs={12}>
                <Grid container={true}>
                    {bygg.hovedVisningsBilde && (
                        <Grid item={true} md={12} sm={6} xs={12}>
                            <Card className={classes.card}>
                                <CardMedia
                                    className={classes.cardMedia}
                                    image={bygg.hovedVisningsBilde}
                                    title={bygg.navn}
                                >
                                    {bygg.energiKarakter && (
                                        <ByggEnergyScore
                                            energiKarakter={bygg.energiKarakter}
                                            andelFossilEl={bygg.andelFossilEl}
                                        />
                                    )}
                                </CardMedia>
                            </Card>
                        </Grid>
                    )}
                    {bygg.latitude && bygg.longitude && (
                        <Grid item={true} md={12} sm={6} xs={12}>
                            <Card className={classes.card}>
                                <CardContent className={classes.cardContent}>
                                    <GoogleMaps
                                        latitude={bygg.latitude}
                                        longitude={bygg.longitude}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {user && KontraktAccess(user) && (
                <Grid container={true}>
                    <Grid item={true} xs={12}>
                        <Block marginVertical="bulky">
                            <ByggKontraktList byggNummer={bygg.byggNummer} />
                        </Block>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

ByggOverview.displayName = 'ByggOverview';
